import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Coffee, Sparkles, ShieldCheck, Check, Star, MapPin, Download } from 'lucide-react';
import { siteConfig, heroSlides, aboutFeatures, testimonials, marketplaces, stats } from '../mock/data';
import SectionTitle from '../components/SectionTitle';
import ProductCard from '../components/ProductCard';
import SectionRenderer from '../components/page-builder/SectionRenderer';
import { trackClick } from '../lib/tracking';
import { fetchProducts, fetchArticles, fetchBranches } from '../api/services';
import { useSettings } from '../context/SettingsContext';
import { getPage } from '../lib/pageDefaults';

const iconMap = { Coffee, Sparkles, ShieldCheck };

export default function Home() {
  const hero = heroSlides[0];
  const { settings } = useSettings();
  const waPhone = settings.whatsapp_number || siteConfig.whatsappNumber;
  const waLink = `https://api.whatsapp.com/send?phone=${waPhone}&text=${encodeURIComponent(siteConfig.waMessage)}`;
  const page = getPage(settings?.page_builder, '/');
  const c = page?.content || {};

  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    fetchProducts().then((d) => setProducts(d.slice(0, 6))).catch(() => {});
    fetchArticles({ limit: 3 }).then(setArticles).catch(() => {});
    fetchBranches().then(setBranches).catch(() => {});
  }, []);

  const heroTitle = c.heroTitle || hero.title;
  const heroSubtitle = c.heroSubtitle || '';
  const heroImage = c.heroImage || hero.image;
  const heroBadge = c.heroBadge || 'Sejak 2013 · Bubuk Minuman Premium';

  return (
    <>
      {/* Hero */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden bg-gradient-to-br from-rose-50 via-white to-yellow-50">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-32 -left-10 w-72 h-72 rounded-full bg-japo-green/10 blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-japo-yellow/20 blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <div className="inline-flex items-center gap-2 bg-white shadow-sm border border-rose-100 rounded-full px-4 py-1.5 mb-6">
                <span className="w-2 h-2 rounded-full bg-japo-green animate-pulse"></span>
                <span className="text-xs font-bold text-japo-green uppercase tracking-widest">{heroBadge}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-[1.1] mb-6 tracking-tight">
                {heroTitle.split(' ').slice(0, 2).join(' ')}{' '}
                <span className="text-japo-green relative">
                  {heroTitle.split(' ').slice(2).join(' ')}
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                    <path d="M2 6 Q100 -2 198 6" stroke="#F2A900" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-xl">
                {heroSubtitle || (
                  <>
                    Bikin pelangganmu <em className="text-japo-green not-italic font-semibold">repeat order</em> hanya dengan bubuk minuman premium terlengkap dari BelBel (Sidoarjo Powder).
                  </>
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick('whatsapp_hero_cta')}
                  className="inline-flex items-center justify-center gap-2 bg-japo-yellow hover:bg-[#D19400] text-gray-900 font-bold px-7 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all group"
                >
                  <MessageCircle className="w-5 h-5" />
                  Pesan Sekarang
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <Link
                  to="/produk"
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-japo-green font-bold px-7 py-4 rounded-xl border-2 border-japo-green shadow-sm hover:shadow-md transition-all"
                >
                  Lihat Semua Produk
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-12 pt-8 border-t border-gray-200">
                {stats.map((s) => (
                  <div key={s.label}>
                    <div className="text-2xl md:text-3xl font-extrabold text-japo-green">{s.value}</div>
                    <div className="text-[11px] md:text-xs text-gray-600 font-medium mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative fade-in-up">
              <div className="relative aspect-square max-w-lg mx-auto">
                <div className="absolute inset-0 bg-japo-green/5 rounded-full blur-3xl"></div>
                <img src={heroImage} alt="BelBel Bubuk Minuman" className="relative w-full h-full object-cover rounded-3xl float-anim" />
                <div className="absolute top-8 -left-4 bg-white rounded-2xl shadow-2xl p-4 items-center gap-3 z-10 hidden md:flex">
                  <div className="w-10 h-10 rounded-full bg-japo-green flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Bersertifikat</div>
                    <div className="font-bold text-sm">Halal & PIRT</div>
                  </div>
                </div>
                <div className="absolute bottom-12 -right-4 bg-white rounded-2xl shadow-2xl p-4 items-center gap-3 z-10 hidden md:flex">
                  <div className="w-10 h-10 rounded-full bg-japo-yellow flex items-center justify-center">
                    <Star className="w-5 h-5 text-gray-900" fill="currentColor" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Rating Pelanggan</div>
                    <div className="font-bold text-sm">4.9 / 5.0</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About / Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionTitle
            eyebrow={c.featuresEyebrow || 'Tentang BelBel (Sidoarjo Powder)'}
            title={c.featuresTitle || 'Supplier Bubuk Minuman Premium'}
            description={c.featuresDescription || 'BelBel adalah brand bubuk minuman dengan kualitas premium. Diproduksi menggunakan bahan baku bermutu tinggi dan diproses secara higienis oleh tenaga produksi profesional.'}
          />
          <div className="grid md:grid-cols-3 gap-6">
            {aboutFeatures.map((f) => {
              const Icon = iconMap[f.icon];
              return (
                <div key={f.title} className="group bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-japo-green card-shadow relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-japo-yellow/10 rounded-full -translate-y-8 translate-x-8 group-hover:scale-150 transition-transform"></div>
                  <div className="relative">
                    <div className="w-14 h-14 rounded-xl bg-japo-green flex items-center justify-center mb-5 group-hover:bg-japo-yellow transition-colors">
                      <Icon className="w-7 h-7 text-white group-hover:text-gray-900" />
                    </div>
                    <h3 className="font-bold text-xl mb-4 text-gray-900">{f.title}</h3>
                    <ul className="space-y-2.5">
                      {f.items.map((item) => (
                        <li key={item} className="flex items-center gap-2 text-gray-700 text-sm">
                          <div className="w-5 h-5 rounded-full bg-japo-green/10 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-japo-green" strokeWidth={3} />
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionTitle
            eyebrow={c.productsEyebrow || 'Produk BelBel'}
            title={c.productsTitle || 'Varian Rasa Pilihan'}
            description={c.productsDescription || 'Beberapa varian favorit yang paling dicintai pelanggan kami. Kualitas premium tanpa filler, tanpa pengawet, tanpa pewarna kimia.'}
          />
          {products.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5 md:gap-6">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Link
              to="/produk"
              className="inline-flex items-center justify-center gap-2 bg-japo-green hover:bg-japo-green-dark text-white font-bold px-7 py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all"
            >
              Lihat Semua Produk
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); trackClick('download_catalog'); }}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-yellow-50 text-japo-green font-bold px-7 py-3.5 rounded-xl border-2 border-japo-yellow transition-all"
            >
              <Download className="w-4 h-4" />
              Download Katalog
            </a>
          </div>
        </div>
      </section>

      {/* Marketplace */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionTitle
                eyebrow={c.marketplaceEyebrow || 'Temukan di Marketplace Favoritmu!'}
                title={c.marketplaceTitle || 'Belanja BelBel Lebih Mudah & Lebih Murah'}
                description={c.marketplaceDescription || 'Pastikan voucher dari marketplace favoritmu tidak hangus sia-sia dan dapatkan produk pilihan BelBel secara mudah dan tentunya dengan harga terbaik.'}
                align="left"
              />
              <p className="font-semibold text-gray-900 mb-4">Pesan produk kami di:</p>
              <div className="grid grid-cols-2 gap-4">
                {marketplaces.map((m) => (
                  <a
                    key={m.name}
                    href={m.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick(`marketplace_${m.name.toLowerCase().replace(/\s+/g, '_')}_hero`)}
                    className={`${m.bg} rounded-2xl p-5 border-2 border-transparent hover:border-japo-green flex flex-col items-center justify-center gap-2 card-shadow text-center`}
                  >
                    <img
                      src={m.logo}
                      alt={m.name}
                      className="w-full max-w-[140px] h-24 object-contain"
                      loading="lazy"
                    />
                    <span className="text-xs text-gray-700 font-medium leading-snug px-1">{m.cta || `Belanja di ${m.name}`}</span>
                  </a>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square max-w-md mx-auto bg-gradient-to-br from-rose-50 to-yellow-50 rounded-3xl p-4 md:p-6 flex items-center justify-center">
                <img
                  src={c.marketplaceImage || '/images/marketplace-belbel.jpg'}
                  alt="Belanja BelBel di Shopee & TikTok Shop"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Marquee */}
      <section className="py-20 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionTitle
            eyebrow={c.testimonialsEyebrow || 'Testimonial'}
            title={c.testimonialsTitle || 'Cerita Mitra BelBel'}
            description={c.testimonialsDescription || 'Sejak 2013, BelBel telah dipercaya oleh banyak pelaku usaha minuman UMKM, franchise/kemitraan, agen/grosir bubuk minuman.'}
          />
        </div>
        <div className="relative">
          <div className="flex marquee-track gap-6 w-max">
            {[...testimonials, ...testimonials].map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 w-[340px] border border-gray-100 shadow-sm flex-shrink-0">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-japo-yellow" fill="currentColor" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4 line-clamp-4">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-japo-green flex items-center justify-center text-white font-bold">
                    {t.name[0]}
                  </div>
                  <div>
                    <div className="font-bold text-sm text-gray-900">{t.name}</div>
                    <div className="text-xs text-gray-500">Varian: {t.flavor}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Branches */}
      {branches.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <SectionTitle
              eyebrow={c.branchesEyebrow || 'Toko Cabang'}
              title={c.branchesTitle || 'Temukan BelBel secara Langsung!'}
              description={c.branchesDescription || 'Kunjungi toko cabang kami di berbagai kota untuk konsultasi produk & pembelian langsung.'}
            />
            <div className="grid md:grid-cols-3 gap-6">
              {branches.slice(0, 3).map((b) => (
                <div key={b.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 card-shadow">
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    <img src={b.image} alt={b.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="text-xs font-bold text-japo-yellow uppercase tracking-widest mb-1">Toko Cabang</div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2">{b.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 flex gap-2">
                      <MapPin className="w-4 h-4 text-japo-green flex-shrink-0 mt-0.5" />
                      <span>{b.address}</span>
                    </p>
                    <div className="flex gap-2">
                      <a
                        href={b.map_link || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-japo-green hover:bg-japo-green-dark text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                      >
                        <MapPin className="w-4 h-4" />
                        Lihat Lokasi
                      </a>
                      <a
                        href={`https://wa.me/${b.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackClick('whatsapp_branch', { branch: b.title })}
                        className="inline-flex items-center justify-center bg-[#25D366] hover:bg-[#1ebe57] text-white p-2.5 rounded-lg transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/agen" className="inline-flex items-center gap-2 text-japo-green font-bold hover:gap-3 transition-all">
                Lihat Semua Agen <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Articles Preview */}
      {articles.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 lg:px-8">
            <SectionTitle
              eyebrow={c.articlesEyebrow || 'Artikel & Tips'}
              title={c.articlesTitle || 'Insight untuk Bisnis Minuman Anda'}
              description={c.articlesDescription || 'Baca artikel terbaru seputar tips bisnis minuman, resep signature, dan panduan produk BelBel.'}
            />
            <div className="grid md:grid-cols-3 gap-6">
              {articles.slice(0, 3).map((a) => (
                <Link key={a.id} to={`/artikel/${a.slug}`} className="group bg-white rounded-2xl overflow-hidden card-shadow border border-gray-100">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                      <span className="bg-japo-green/10 text-japo-green font-bold px-2.5 py-1 rounded-full">{a.category}</span>
                      <span>{a.date}</span>
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2 leading-snug group-hover:text-japo-green transition-colors line-clamp-2">{a.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-4">{a.excerpt}</p>
                    <span className="text-japo-green font-bold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      Baca Selengkapnya <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-japo-green relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 rounded-full bg-japo-yellow blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
              {c.ctaTitle || 'Siap Kembangkan Bisnis Minuman Anda?'}
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              {c.ctaDescription || 'Konsultasi gratis dengan tim BelBel untuk mendapatkan penawaran terbaik untuk usaha minuman Anda.'}
            </p>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('whatsapp_cta_bottom')}
              className="inline-flex items-center gap-3 bg-japo-yellow hover:bg-[#D19400] text-gray-900 font-extrabold px-8 py-4 rounded-xl shadow-2xl hover:shadow-yellow-500/30 transition-all group text-lg"
            >
              <MessageCircle className="w-5 h-5" />
              {c.ctaButton || 'Chat WhatsApp Sekarang'}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      <SectionRenderer sections={page?.sections || []} />
    </>
  );
}
